import React from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import SpinningRoundedBorder from '#/components/basic/animations/SpinningRoundedBorder';
import SimpleModal from '#/components/basic/modal/SimpleModal';
import { getWalletIcon } from '#/components/wallet/icons/walletIcon';
import config from '#/config';
import { ChainsFromConfig } from '#/config/configType';
import { useDiscoverEip6961WalletByRdns } from '#/lib/ethereum/walletDiscovery/walletDiscovery';
import { useSelector } from '#/reduxState/store';
import { WalletType } from '#/reduxState/wallet/walletUtils';

import ReconnectWalletButton from './ReconnectWalletButton';

const Wrap = styled.div`
  text-align: left;
  .isMobileStyle & {
    text-align: center;
  }
`;

const blockStyle = css`
  padding: 16px 52px;
  background: var(--mainDark);
  border-radius: 5px;
  font-family: var(--standardFontFamily);
  font-style: normal;
  font-weight: 400;
  font-size: var(--medium);
  line-height: 1.5;
  color: var(--white);
`;

const BlockText = styled.div`
  ${blockStyle}
  text-align: center;
`;

const NormalText = styled.div`
  padding: 0 16px 12px;
  border-radius: 5px;
  font-style: normal;
  font-weight: 400;
  font-size: var(--medium);
  line-height: 1.5;
  color: var(--white);
  text-align: center;
`;

// const TypedData = styled.pre`
//   max-width: 100vw;
//   ${blockStyle}
//   text-align: left;
//   white-space: pre-wrap;
//   overflow: scroll;
//   ${scrollbarStyle}
// `;

const SpinWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 18px;
  padding: 12px 0 32px 0;

  & img {
    opacity: 0.7;
  }
  & img:hover {
    opacity: 1;
  }
`;

type ComponentProps = {
  signingText?: string;
  signingHash?: string;
  signingTyped?: boolean;
  signingTransaction?: ChainsFromConfig;
  walletType: WalletType | null;
  walletDiscoveryRdns: string | undefined;
};

export function WalletSignPromptOverlayModalComponent({
  signingText,
  signingHash,
  signingTyped,
  signingTransaction,
  walletType,
  walletDiscoveryRdns,
}: ComponentProps) {
  const walletDiscovered = useDiscoverEip6961WalletByRdns(
    walletDiscoveryRdns,
    true
  );

  if (!signingText && !signingHash && !signingTyped && !signingTransaction) {
    return null;
  }
  return (
    <Wrap>
      {/* <h1>{title || 'Please sign on your device'}</h1> */}
      <SpinWrap>
        <SpinningRoundedBorder>
          {walletType && (
            <img
              src={getWalletIcon(walletType, walletDiscovered?.info.icon)}
              alt="Wallet"
              style={{ maxWidth: 40 }}
            />
          )}
        </SpinningRoundedBorder>
      </SpinWrap>

      {signingTransaction && (
        <NormalText>
          Please sign the {config.chains[signingTransaction].chainNetwork}{' '}
          transaction
        </NormalText>
      )}

      {signingTyped && <NormalText>{'Please sign on your device'}</NormalText>}

      {signingText && (
        <BlockText>
          {signingText.split(/\n/).map(line => (
            <div key={line} style={{ padding: '2px 0' }}>
              {line}
            </div>
          ))}
        </BlockText>
      )}

      {/* <InfoPanel>This won't cost you any gas fees.</InfoPanel> */}
    </Wrap>
  );
}

export function useWalletSignPromptModal() {
  const signatureModal = useSelector(state => state.exchange.signatureModal);
  const walletType = useSelector(state => state.wallet.walletType);
  const walletDiscoveryRdns = useSelector(
    state => state.wallet.walletDiscoveryRdns
  );

  if (
    !signatureModal.signingText &&
    !signatureModal.signingHash &&
    !signatureModal.signingTyped &&
    !signatureModal.signingTransaction
  ) {
    return {
      walletSignPromptPlaceholder: null,
    };
  }

  return {
    walletSignPromptPlaceholder: (
      <>
        <WalletSignPromptOverlayModalComponent
          signingText={signatureModal.signingText}
          signingHash={signatureModal.signingHash}
          signingTyped={signatureModal.signingTyped}
          signingTransaction={signatureModal.signingTransaction}
          walletType={walletType}
          walletDiscoveryRdns={walletDiscoveryRdns}
        />
        <ReconnectWalletButton />
      </>
    ),
    walletSignTitle: signatureModal.title,
  };
}

function WalletSignPromptOverlayModal() {
  const { pathname } = useLocation();
  const unlockModalOpen = useSelector(
    state => state.walletDetail.unlockModalOpen
  );
  const { walletSignPromptPlaceholder, walletSignTitle } =
    useWalletSignPromptModal();

  if (unlockModalOpen || pathname.includes('/unlock')) {
    return null;
  }

  if (!walletSignPromptPlaceholder) {
    return null;
  }

  return (
    <SimpleModal zIndex={199999} size="small" title={walletSignTitle}>
      {walletSignPromptPlaceholder}
    </SimpleModal>
  );
}

export default WalletSignPromptOverlayModal;
